<script lang="ts">
export default {
  name: 'ConvertNewCoverageAndRenewalTowerModal',
  components: {
    AppModal,
    AppSelect
  }
};
</script>

<script setup lang="ts">
import { ref, watch } from 'vue';
import AppModal from '@/components/v2/AppModal.vue';
import AppSelect from '@/components/v2/AppSelect.vue';
import { accountService } from '@/services/api';
import { onMounted } from 'vue';
import { onUnmounted } from 'vue';
import { ProgramSummary } from '@/types/Programs';

const title = 'Convert Tower';

const props = defineProps<{
  show: boolean;
  towerId?: string;
  accountId?: number;
}>();

const emit = defineEmits<{
  (e: 'cancel'): void;
  (e: 'convert', towerId: string | undefined, programId: number | undefined): void;
}>();

// -----
// refs
// -----
const programId = ref<number>();
const programs = ref<ProgramSummary[]>([]);

// ---------
// watchers
// ---------
watch(
  () => props.show,
  async (show) => {
    if (show) {
      if (props.accountId) {
        programs.value = await accountService.getProgramSummary(props.accountId);
      }
    } else {
      programId.value = undefined;
    }
  },
);

// ---------
// lifecycles
// ---------
onMounted(async () => {
  addEventListener('keyup', handleCloseWithEscapeKey);
});

onUnmounted(() => {
  removeEventListener('keyup', handleCloseWithEscapeKey);
});

// ---------
// handlers
// ---------
function handleCloseWithEscapeKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    emit('cancel');
  }
}
</script>

<template>
  <AppModal
    :show="show"
    :title="title"
    :handle-cancel="() => emit('cancel')"
    :handle-action="() => emit('convert', props.towerId, programId)"
    action-button-label="Convert"
  >
    <template #content>
      <div class="flex flex-col gap-3">
        <p class="text-lg text-gray-600">Choose the program you want to link to this tower.</p>
        <div class="w-full">
          <label class="label">
            <span class="label-text text-xs text-gray-500"
              >Program <span class="text-primary">*</span></span
            >
          </label>
          <AppSelect
            v-model="programId"
            :options="programs.map(program => ({ value: program.programId, label: program.programName }))"
            class="w-full h-9"
            placeholder="Select program"
          />
        </div>
      </div>
    </template>
  </AppModal>
</template>
